/**
 * @file Root React provider for the upload manager.
 * Wires Uppy events, hydration/recovery, stall detection, session completion, and context.
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import type { UppyFile } from '@uppy/core';
import type Uppy from '@uppy/core';

import { toast } from '@surface/components';

import { useAnalytics } from '@/lib/analytics/use-analytics';

import {
  getUploadRecoveryTimeoutMs,
  UPLOAD_SESSION_COMPLETION_RETRY_DELAY_MS,
  UPLOAD_STALL_TIMEOUT_MS,
  UPLOAD_STARTED_TOAST_DESCRIPTION,
  UPLOAD_STARTED_TOAST_TITLE,
} from './constants';
import { formatUppyUploadError } from './format-upload-error';
import { recoverLegacyIndexedDBBlobs } from './golden-retriever-recovery';
import {
  getRestorableUppyFileIds,
  reconcileHydratedSessions,
  resumeRestoredUploads,
  tryUploadPendingRestorable,
  UPLOAD_STALLED_ERROR_MESSAGE,
  waitForGoldenRetrieverRestore,
} from './hydrate-upload-sessions';
import {
  deleteMultipartContext,
  loadAllMultipartContexts,
  loadPersistedUploadSessions,
  savePersistedUploadSessions,
} from './persist-upload-sessions';
import { UPLOAD_ACTIVE_STATUSES, UPLOAD_IN_PROGRESS_STATUSES } from './types';
import {
  getUploadAnalyticsFeature,
  getUploadSessionCompletedOutcome,
} from './upload-analytics';
import { getUploadKindAdapter } from './upload-kind-registry';
import { UploadManagerContext } from './upload-manager-context';
import {
  ensureUploadManagerUppy,
  getUploadManagerUppy,
  initUploadManager,
  uploadManagerPartUrlStore,
  uploadManagerPersistedContexts,
} from './upload-manager-instance';
import {
  countActiveUploads,
  mergeHydratedWithBootEnqueues,
  uploadManagerReducer,
} from './upload-manager-reducer';
import {
  getPreviouslyNotifiedSessionCompletions,
  shouldRunSessionCompletionHandler,
  summarizeUploadSession,
} from './upload-session-outcome';
import { shouldShowUploadStartedToast } from './upload-started-toast';

import type { EnqueueUploadParams, UploadResourceSession } from './types';
import type { UploadKindAdapter } from './upload-kind-adapter';

type Meta = Record<string, unknown>;
type Body = Record<string, unknown>;

const startUpload = (uppy: Uppy<Meta, Body>) => {
  void uppy.upload().catch((error: Error & { isRestriction?: boolean }) => {
    if (error.isRestriction) {
      return;
    }

    toast.error('Upload failed', {
      description: error.message || 'Something went wrong while uploading.',
    });
  });
};

const isTerminalUppyFile = (file: UppyFile<Meta, Body>) =>
  Boolean(file.progress?.uploadComplete || file.error != null || file.isGhost);

const removeTerminalUppyFilesForResource = (
  uppy: Uppy<Meta, Body>,
  resourceKey: string,
  removeFile: (uppyFileId: string) => void
) => {
  uppy
    .getFiles()
    .filter(
      (file) =>
        file.meta?.resourceKey === resourceKey && isTerminalUppyFile(file)
    )
    .forEach((file) => {
      removeFile(file.id);
    });
};

type UploadsProviderProps = {
  children: ReactNode;
  /** Feature upload kind adapters to register at initialization. */
  kinds: UploadKindAdapter[];
};

/**
 * Root provider for background uploads. Mount once near app root.
 * Registers adapters, hydrates persisted sessions, and exposes context via {@link useUploadManager}.
 */
export const UploadsProvider = ({ children, kinds }: UploadsProviderProps) => {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const [state, dispatch] = useReducer(uploadManagerReducer, { sessions: [] });
  const [isReady, setIsReady] = useState(false);
  const [activityPanelOpenNonce, setActivityPanelOpenNonce] = useState(0);

  const initializedRef = useRef(false);
  if (!initializedRef.current) {
    initUploadManager(kinds);
    initializedRef.current = true;
  }

  const openUploadsPanel = useCallback(() => {
    setActivityPanelOpenNonce((nonce) => nonce + 1);
  }, []);

  const partUrlStoreRef = useRef(uploadManagerPartUrlStore);
  const persistedContextsRef = useRef(uploadManagerPersistedContexts);
  const handledCompletionsRef = useRef<Map<string, string>>(new Map());
  const trackedSessionCompletionsRef = useRef<Map<string, string>>(new Map());
  const inFlightCompletionRef = useRef<Set<string>>(new Set());
  const completionRetryTimeoutsRef = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const [completionRetryNonce, setCompletionRetryNonce] = useState(0);
  const hydrationCompleteRef = useRef(false);
  const recoveryCompletedAtRef = useRef<number | null>(null);
  const resumeAttemptedRef = useRef(false);
  const fileActivityAtRef = useRef<Map<string, number>>(new Map());
  const sessionsRef = useRef<UploadResourceSession[]>([]);
  const bootEnqueuedSessionIdsRef = useRef<Set<string>>(new Set());

  sessionsRef.current = state.sessions;

  const getUppy = useCallback(() => getUploadManagerUppy(), []);

  const touchFileActivity = useCallback((uppyFileId: string) => {
    fileActivityAtRef.current.set(uppyFileId, Date.now());
  }, []);

  const syncFileState = useCallback(
    (
      file: UppyFile<Meta, Body> | undefined,
      patch: {
        status?:
          | 'queued'
          | 'uploading'
          | 'paused'
          | 'complete'
          | 'error'
          | 'cancelled';
        progress?: number;
        errorMessage?: string;
      }
    ) => {
      if (!file?.id) {
        return;
      }

      if (
        patch.status === 'complete' ||
        patch.status === 'error' ||
        patch.status === 'cancelled'
      ) {
        fileActivityAtRef.current.delete(file.id);
      } else if (
        patch.status === 'uploading' ||
        patch.status === 'queued' ||
        patch.status === 'paused'
      ) {
        touchFileActivity(file.id);
      }

      dispatch({
        type: 'UPDATE_FILE',
        uppyFileId: file.id,
        patch: {
          ...patch,
          fileName: file.name ?? 'Unknown file',
          bytesTotal: file.size ?? 0,
        },
      });
    },
    [touchFileActivity]
  );

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const uppy = getUppy();

    const onProgress = (
      file: UppyFile<Meta, Body> | undefined,
      progress: { bytesUploaded: number; bytesTotal: number | null }
    ) => {
      if (!file?.id || !progress.bytesTotal) {
        return;
      }

      touchFileActivity(file.id);

      syncFileState(file, {
        status: file.isPaused ? 'paused' : 'uploading',
        progress: Math.round(
          (progress.bytesUploaded / progress.bytesTotal) * 100
        ),
      });
    };

    const onSuccess = (file: UppyFile<Meta, Body> | undefined) => {
      syncFileState(file, { status: 'complete', progress: 100 });
    };

    const onError = (
      file: UppyFile<Meta, Body> | undefined,
      error: { message?: string; name?: string },
      response?: { status?: number; body?: unknown } | XMLHttpRequest
    ) => {
      syncFileState(file, {
        status: 'error',
        errorMessage: formatUppyUploadError(error, response),
      });
    };

    const onUploadStart = (
      _uploadId: string,
      files: UppyFile<Meta, Body>[]
    ) => {
      files.forEach((file) => {
        if (file.id) {
          touchFileActivity(file.id);
        }
        syncFileState(file, { status: 'uploading', progress: 0 });
      });
    };

    uppy.on('upload-progress', onProgress);
    uppy.on('upload-success', onSuccess);
    uppy.on('upload-error', onError);
    uppy.on('upload', onUploadStart);

    return () => {
      uppy.off('upload-progress', onProgress);
      uppy.off('upload-success', onSuccess);
      uppy.off('upload-error', onError);
      uppy.off('upload', onUploadStart);
    };
  }, [getUppy, isReady, syncFileState, touchFileActivity]);

  const hydrateSessionsFromUppy = useCallback(
    (
      uppy: Uppy<Meta, Body>,
      persistedSessions: UploadResourceSession[],
      recoveryResult: 'no-recovery' | 'restored' | 'timeout',
      currentSessions: UploadResourceSession[]
    ) => {
      const sessionsMap = reconcileHydratedSessions(
        persistedSessions,
        uppy.getFiles(),
        persistedContextsRef.current,
        { markNonRestorable: recoveryResult === 'timeout' }
      );

      let sessions = Array.from(sessionsMap.values());
      sessions = mergeHydratedWithBootEnqueues(
        sessions,
        currentSessions,
        bootEnqueuedSessionIdsRef.current
      );

      if (sessions.length === 0) {
        return;
      }

      const hydratedAt = Date.now();
      sessions.forEach((session) => {
        session.files.forEach((file) => {
          if (UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)) {
            fileActivityAtRef.current.set(file.uppyFileId, hydratedAt);
          }
        });
      });

      dispatch({
        type: 'HYDRATE_SESSIONS',
        sessions,
      });
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    let resumeCleanup: (() => void) | undefined;
    let uppy: Uppy<Meta, Body> | undefined;

    const onLateRestored = () => {
      if (!uppy) {
        return;
      }

      hydrateSessionsFromUppy(
        uppy,
        loadPersistedUploadSessions(),
        'restored',
        sessionsRef.current
      );

      if (!resumeAttemptedRef.current) {
        resumeAttemptedRef.current = true;
        resumeCleanup = resumeRestoredUploads(
          uppy,
          persistedContextsRef.current
        );
        return;
      }

      tryUploadPendingRestorable(uppy, persistedContextsRef.current, {
        force: true,
      });
    };

    const bootstrap = async () => {
      try {
        uppy = await ensureUploadManagerUppy();
        if (cancelled) {
          return;
        }

        uppy.on('restored', onLateRestored);

        const persistedSessions = loadPersistedUploadSessions();
        const inFlightFileCount = persistedSessions.reduce(
          (count, session) =>
            count +
            session.files.filter((file) =>
              UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)
            ).length,
          0
        );
        const recoveryTimeoutMs = getUploadRecoveryTimeoutMs(
          Math.max(inFlightFileCount, uppy.getFiles().length)
        );

        const persisted = await loadAllMultipartContexts();
        if (cancelled) {
          return;
        }

        persisted.forEach((context) => {
          persistedContextsRef.current.set(context.uppyFileId, context);
          partUrlStoreRef.current[context.uploadId] = {
            ...context.partUrlStore,
          };
        });

        const recoveryResult = await waitForGoldenRetrieverRestore(
          uppy,
          recoveryTimeoutMs
        );

        if (cancelled) {
          return;
        }

        await recoverLegacyIndexedDBBlobs(uppy);

        if (cancelled) {
          return;
        }

        const restoredFiles = uppy.getFiles();

        getPreviouslyNotifiedSessionCompletions(persistedSessions).forEach(
          (completedAt, sessionId) => {
            handledCompletionsRef.current.set(sessionId, completedAt);
            trackedSessionCompletionsRef.current.set(sessionId, completedAt);
          }
        );

        hydrateSessionsFromUppy(
          uppy,
          persistedSessions,
          recoveryResult,
          sessionsRef.current
        );

        const restorableCount = getRestorableUppyFileIds(
          restoredFiles,
          persistedContextsRef.current
        ).length;
        const shouldResume =
          recoveryResult !== 'timeout' || restorableCount > 0;

        if (shouldResume && !resumeAttemptedRef.current) {
          resumeAttemptedRef.current = true;
          resumeCleanup = resumeRestoredUploads(
            uppy,
            persistedContextsRef.current
          );
        }
      } catch (error) {
        const recoveryError =
          error instanceof Error ? error : new Error('Upload recovery failed');
        Sentry.captureException(recoveryError, {
          tags: {
            component: 'uploads',
            feature: 'recovery',
          },
        });
        // eslint-disable-next-line no-console
        console.warn(
          '[Uploads] Upload recovery failed; starting in degraded mode:',
          recoveryError
        );
      } finally {
        if (!cancelled) {
          recoveryCompletedAtRef.current = Date.now();
          hydrationCompleteRef.current = true;
          setIsReady(true);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      resumeCleanup?.();
      uppy?.off('restored', onLateRestored);
      if (!hydrationCompleteRef.current) {
        resumeAttemptedRef.current = false;
      }
    };
  }, [hydrateSessionsFromUppy]);

  const scheduleSessionCompletionRetry = useCallback(
    (sessionId: string, completedAt: string) => {
      if (
        handledCompletionsRef.current.get(sessionId) === completedAt ||
        completionRetryTimeoutsRef.current.has(sessionId)
      ) {
        return;
      }

      const timeoutId = setTimeout(() => {
        completionRetryTimeoutsRef.current.delete(sessionId);
        setCompletionRetryNonce((nonce) => nonce + 1);
      }, UPLOAD_SESSION_COMPLETION_RETRY_DELAY_MS);

      completionRetryTimeoutsRef.current.set(sessionId, timeoutId);
    },
    []
  );

  useEffect(() => {
    const retryTimeouts = completionRetryTimeoutsRef.current;

    return () => {
      retryTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      retryTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      for (const session of state.sessions) {
        if (cancelled) {
          return;
        }

        if (
          !shouldRunSessionCompletionHandler(
            session,
            handledCompletionsRef.current,
            inFlightCompletionRef.current
          )
        ) {
          continue;
        }

        const adapter = getUploadKindAdapter(session.kind);
        const outcome = summarizeUploadSession(session);
        const onComplete = adapter?.onSessionComplete;
        const completedAt = session.completedAt!;

        if (
          trackedSessionCompletionsRef.current.get(session.id) !== completedAt
        ) {
          trackedSessionCompletionsRef.current.set(session.id, completedAt);
          track('uploads_session_completed', {
            feature: getUploadAnalyticsFeature(session.kind),
            upload_kind: session.kind,
            total_count: outcome.totalCount,
            complete_count: outcome.completeCount,
            error_count: outcome.errorCount,
            cancelled_count: outcome.cancelledCount,
            outcome: getUploadSessionCompletedOutcome(outcome),
          });
        }

        if (!onComplete) {
          handledCompletionsRef.current.set(session.id, completedAt);
          dispatch({ type: 'PRUNE' });
          continue;
        }

        inFlightCompletionRef.current.add(session.id);

        try {
          await onComplete(session, { queryClient, outcome });

          if (cancelled) {
            return;
          }

          handledCompletionsRef.current.set(session.id, completedAt);
          dispatch({ type: 'PRUNE' });
        } catch (error) {
          const completionError =
            error instanceof Error
              ? error
              : new Error('Upload session completion handler failed');

          Sentry.captureException(completionError, {
            tags: {
              component: 'uploads',
              feature: 'session-complete',
            },
            extra: {
              sessionId: session.id,
              sessionKind: session.kind,
            },
          });

          // eslint-disable-next-line no-console
          console.warn(
            `[Uploads] Session completion handler failed for ${session.id}; will retry:`,
            completionError
          );

          if (!cancelled) {
            scheduleSessionCompletionRetry(session.id, session.completedAt!);
          }
        } finally {
          inFlightCompletionRef.current.delete(session.id);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    queryClient,
    scheduleSessionCompletionRetry,
    state.sessions,
    completionRetryNonce,
    track,
  ]);

  useEffect(() => {
    if (!isReady || !hydrationCompleteRef.current) {
      return;
    }
    savePersistedUploadSessions(state.sessions);
  }, [isReady, state.sessions]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const uppy = getUppy();
    const interval = setInterval(() => {
      const now = Date.now();

      state.sessions.forEach((session) => {
        session.files.forEach((file) => {
          if (!UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)) {
            return;
          }

          const recoveryCompletedAt = recoveryCompletedAtRef.current;
          const lastActivity = fileActivityAtRef.current.get(file.uppyFileId);
          const activityBaseline = Math.max(
            lastActivity ?? 0,
            recoveryCompletedAt ?? 0
          );
          if (now - activityBaseline < UPLOAD_STALL_TIMEOUT_MS) {
            return;
          }

          const uppyFile = uppy.getFile(file.uppyFileId);
          const sessionHasActiveUploads = session.files.some(
            (entry) =>
              entry.uppyFileId !== file.uppyFileId &&
              entry.status === 'uploading'
          );

          if (
            file.status === 'queued' &&
            (sessionHasActiveUploads || uppyFile?.progress?.uploadStarted)
          ) {
            return;
          }

          dispatch({
            type: 'UPDATE_FILE',
            uppyFileId: file.uppyFileId,
            patch: {
              status: 'error',
              errorMessage: UPLOAD_STALLED_ERROR_MESSAGE,
            },
          });
          fileActivityAtRef.current.delete(file.uppyFileId);
        });
      });
    }, 15_000);

    return () => {
      clearInterval(interval);
    };
  }, [getUppy, isReady, state.sessions]);

  const removeUppyFile = useCallback(
    (uppyFileId: string, uppy: Uppy<Meta, Body>) => {
      try {
        uppy.removeFile(uppyFileId);
      } catch {
        // File may already be removed from Uppy.
      }
      persistedContextsRef.current.delete(uppyFileId);
      void deleteMultipartContext(uppyFileId);
      dispatch({ type: 'REMOVE_FILE', uppyFileId });
    },
    []
  );

  const enqueue = useCallback(
    (params: EnqueueUploadParams) => {
      void ensureUploadManagerUppy().then((uppy) => {
        const {
          kind,
          resourceKey,
          label,
          returnPath,
          meta,
          files,
          source,
          entryPoint,
        } = params;
        if (files.length === 0) {
          return;
        }

        if (!hydrationCompleteRef.current) {
          bootEnqueuedSessionIdsRef.current.add(resourceKey);
        }

        removeTerminalUppyFilesForResource(uppy, resourceKey, (uppyFileId) =>
          removeUppyFile(uppyFileId, uppy)
        );

        const adapter = getUploadKindAdapter(kind);

        dispatch({
          type: 'ENSURE_SESSION',
          session: {
            id: resourceKey,
            kind,
            label,
            returnPath,
            meta,
            dismissed: false,
          },
        });

        let queuedCount = 0;
        const queuedFiles: File[] = [];

        files.forEach((file) => {
          try {
            const uppyFileId = uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              meta: {
                ...(adapter?.buildFileMeta(file, meta) ?? {}),
                returnPath,
                sessionLabel: label,
                uploadKind: kind,
                resourceKey,
              },
            });

            dispatch({
              type: 'ADD_FILE',
              resourceKey,
              file: {
                uppyFileId,
                fileName: file.name,
                bytesTotal: file.size,
                progress: 0,
                status: 'queued',
              },
            });
            touchFileActivity(uppyFileId);
            queuedCount += 1;
            queuedFiles.push(file);
          } catch (error) {
            toast.error('Could not add file', {
              description:
                error instanceof Error
                  ? error.message
                  : 'Failed to queue upload.',
            });
          }
        });

        if (queuedCount === 0) {
          return;
        }

        track('uploads_files_enqueued', {
          feature: getUploadAnalyticsFeature(kind),
          upload_kind: kind,
          file_count: queuedCount,
          source,
          entry_point: entryPoint,
        });

        const retryTimeoutId =
          completionRetryTimeoutsRef.current.get(resourceKey);
        if (retryTimeoutId) {
          clearTimeout(retryTimeoutId);
          completionRetryTimeoutsRef.current.delete(resourceKey);
        }

        if (shouldShowUploadStartedToast(queuedFiles)) {
          toast.success(UPLOAD_STARTED_TOAST_TITLE, {
            description: UPLOAD_STARTED_TOAST_DESCRIPTION,
          });
        }

        startUpload(uppy);
      });
    },
    [removeUppyFile, touchFileActivity, track]
  );

  const cancelFile = useCallback(
    (uppyFileId: string) => {
      const uppy = getUppy();
      const file = uppy.getFile(uppyFileId);
      const uploadKind =
        typeof file?.meta?.uploadKind === 'string'
          ? file.meta.uploadKind
          : undefined;

      if (uploadKind) {
        track('uploads_file_cancelled', {
          feature: getUploadAnalyticsFeature(uploadKind),
          upload_kind: uploadKind,
        });
      }

      removeUppyFile(uppyFileId, uppy);
    },
    [getUppy, removeUppyFile, track]
  );

  const pauseResumeFile = useCallback(
    (uppyFileId: string) => {
      const uppy = getUppy();
      uppy.pauseResume(uppyFileId);
      const file = uppy.getFile(uppyFileId);
      syncFileState(file, {
        status: file?.isPaused ? 'paused' : 'uploading',
        progress: file?.progress?.percentage ?? 0,
      });
    },
    [getUppy, syncFileState]
  );

  const dismissSession = useCallback((resourceKey: string) => {
    handledCompletionsRef.current.delete(resourceKey);
    trackedSessionCompletionsRef.current.delete(resourceKey);
    dispatch({ type: 'DISMISS_SESSION', resourceKey });
  }, []);

  const cancelSession = useCallback(
    (resourceKey: string) => {
      const session = state.sessions.find((item) => item.id === resourceKey);
      if (!session) {
        return;
      }

      const activeFiles = session.files.filter((file) =>
        UPLOAD_ACTIVE_STATUSES.includes(file.status)
      );

      if (activeFiles.length > 0) {
        track('uploads_session_cancelled', {
          feature: getUploadAnalyticsFeature(session.kind),
          upload_kind: session.kind,
          file_count: activeFiles.length,
        });
      }

      // Cancel without per-file analytics — session-level event already fired.
      activeFiles.forEach((file) => {
        removeUppyFile(file.uppyFileId, getUppy());
      });
    },
    [getUppy, removeUppyFile, state.sessions, track]
  );

  const value = useMemo(
    () => ({
      sessions: state.sessions,
      activeUploadCount: countActiveUploads(state.sessions),
      activityPanelOpenNonce,
      openUploadsPanel,
      enqueue,
      cancelFile,
      cancelSession,
      pauseResumeFile,
      dismissSession,
      isReady,
    }),
    [
      cancelFile,
      cancelSession,
      dismissSession,
      enqueue,
      isReady,
      openUploadsPanel,
      pauseResumeFile,
      state.sessions,
      activityPanelOpenNonce,
    ]
  );

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
};

/** @deprecated Use {@link UploadsProvider}. */
export const UploadManagerProvider = UploadsProvider;

/** @deprecated Use {@link UploadsProvider}. */
export const FileTransfersProvider = UploadsProvider;
